import type { components } from "@/api-schema";
import { BranchIdentifierSystem } from "@/api-schema-enums";
import {
  normalizeNumericIdentifier,
  normalizePositiveSafeIntegerIdentifier,
} from "@/common/helpers/normalize-numeric-identifier";
import { z } from "zod";

export { normalizeNumericIdentifier } from "@/common/helpers/normalize-numeric-identifier";

type Branch = Pick<components["schemas"]["Branch"], "branchIdentifiers" | "uuid">;
export type BranchIdentifier = components["schemas"]["BranchIdentifier"];
export type BranchIdentifierDto = components["schemas"]["BranchIdentifierDto"];

const optionalIdentifierSchema = z.string().trim();
const optionalNumericIdentifierSchema = optionalIdentifierSchema.refine(
  (value) => value === "" || normalizeNumericIdentifier(value) !== undefined,
  "masterdata:BranchNumericIdentifierMustBeNonNegativeInteger",
);

export const branchIdentifierFormSchema = z.object({
  v1: optionalIdentifierSchema.refine(
    (value) => value === "" || normalizeBranchV1Identifier(value) !== undefined,
    "masterdata:BranchV1IdentifierMustBeInteger",
  ),
  formel1Ahg: optionalNumericIdentifierSchema,
  formel1Ghg: optionalNumericIdentifierSchema,
  cross: optionalNumericIdentifierSchema,
  fahrschule: optionalIdentifierSchema,
  carano: optionalIdentifierSchema,
  sap: optionalNumericIdentifierSchema,
});

export type BranchIdentifierFormValues = z.infer<typeof branchIdentifierFormSchema>;

export const branchIdentifierFormDefaultValues: BranchIdentifierFormValues = {
  v1: "",
  formel1Ahg: "",
  formel1Ghg: "",
  cross: "",
  fahrschule: "",
  carano: "",
  sap: "",
};

export function normalizeBranchV1Identifier(identifier: string): string | undefined {
  return normalizePositiveSafeIntegerIdentifier(identifier);
}

export function normalizeBranchFahrschuleIdentifier(identifier: string): string | undefined {
  const value = identifier.trim().toUpperCase();
  return value || undefined;
}

export function normalizeBranchIdentifier(type: BranchIdentifierSystem, identifier: string): string | undefined {
  if (type === BranchIdentifierSystem.V1) {
    return normalizeBranchV1Identifier(identifier);
  }

  if (type === BranchIdentifierSystem.FAHRSCHULE) {
    return normalizeBranchFahrschuleIdentifier(identifier);
  }

  if (
    type === BranchIdentifierSystem.Formel1Ahg ||
    type === BranchIdentifierSystem.Formel1Ghg ||
    type === BranchIdentifierSystem.CROSS ||
    type === BranchIdentifierSystem.Sap
  ) {
    return normalizeNumericIdentifier(identifier);
  }

  const value = identifier.trim();
  return value || undefined;
}

export function getBranchIdentifier(
  branch: Pick<Branch, "branchIdentifiers">,
  type: BranchIdentifierSystem,
): string | undefined {
  return branch.branchIdentifiers?.find((identifier) => identifier.type === type)?.identifier;
}

export function getBranchV1Identifier(branch: Pick<Branch, "branchIdentifiers">): string | undefined {
  return getBranchIdentifier(branch, BranchIdentifierSystem.V1);
}

export function getBranchIdentifierKey(type: BranchIdentifierSystem, identifier: string): string | undefined {
  const normalizedIdentifier = normalizeBranchIdentifier(type, identifier);
  return normalizedIdentifier === undefined ? undefined : `${type}\u0000${normalizedIdentifier}`;
}

export function isDuplicateBranchIdentifier(
  duplicateKeys: ReadonlySet<string>,
  type: BranchIdentifierSystem,
  identifier: string,
): boolean {
  const key = getBranchIdentifierKey(type, identifier);
  return key !== undefined && duplicateKeys.has(key);
}

export function toBranchIdentifierFormValues(
  branchIdentifiers?: ReadonlyArray<Pick<BranchIdentifier, "type" | "identifier">>,
): BranchIdentifierFormValues {
  const getIdentifier = (type: BranchIdentifierSystem) =>
    branchIdentifiers?.find((identifier) => identifier.type === type)?.identifier ?? "";

  return {
    v1: getIdentifier(BranchIdentifierSystem.V1),
    formel1Ahg: getIdentifier(BranchIdentifierSystem.Formel1Ahg),
    formel1Ghg: getIdentifier(BranchIdentifierSystem.Formel1Ghg),
    cross: getIdentifier(BranchIdentifierSystem.CROSS),
    fahrschule: getIdentifier(BranchIdentifierSystem.FAHRSCHULE),
    carano: getIdentifier(BranchIdentifierSystem.CARANO),
    sap: getIdentifier(BranchIdentifierSystem.Sap),
  };
}

export function toBranchIdentifierDtos(values: BranchIdentifierFormValues): BranchIdentifierDto[] {
  const identifiers: BranchIdentifierDto[] = [];

  const appendIdentifier = (type: BranchIdentifierSystem, identifier: string) => {
    const normalizedIdentifier = normalizeBranchIdentifier(type, identifier);
    if (normalizedIdentifier !== undefined) {
      identifiers.push({ type, identifier: normalizedIdentifier });
    }
  };

  appendIdentifier(BranchIdentifierSystem.V1, values.v1);
  appendIdentifier(BranchIdentifierSystem.Formel1Ahg, values.formel1Ahg);
  appendIdentifier(BranchIdentifierSystem.Formel1Ghg, values.formel1Ghg);
  appendIdentifier(BranchIdentifierSystem.CROSS, values.cross);
  appendIdentifier(BranchIdentifierSystem.FAHRSCHULE, values.fahrschule);
  appendIdentifier(BranchIdentifierSystem.CARANO, values.carano);
  appendIdentifier(BranchIdentifierSystem.Sap, values.sap);

  return identifiers;
}

export function getDuplicateBranchIdentifierKeys(
  branches: ReadonlyArray<Branch> | undefined,
  values: BranchIdentifierFormValues,
  excludeBranchUuid?: string,
): Set<string> {
  if (!branches) return new Set();

  const existingKeys = new Set(
    branches
      .filter((branch) => branch.uuid !== excludeBranchUuid)
      .flatMap((branch) => branch.branchIdentifiers ?? [])
      .map((identifier) => getBranchIdentifierKey(identifier.type, identifier.identifier))
      .filter((key): key is string => key !== undefined),
  );

  return new Set(
    toBranchIdentifierDtos(values)
      .map((identifier) => getBranchIdentifierKey(identifier.type, identifier.identifier))
      .filter((key): key is string => key !== undefined && existingKeys.has(key)),
  );
}
